import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiEdit, FiUsers, FiSettings, FiActivity, FiPackage, FiTag, FiCalendar, FiStar, FiCopy, FiExternalLink } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { extensionsAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import './ExtensionDetails.css';

const ExtensionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { partner } = useContext(AuthContext);
  const [extension, setExtension] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchExtensionDetails();
  }, [id]);

  const fetchExtensionDetails = async () => {
    try {
      setLoading(true);
      const response = await extensionsAPI.getById(id);
      
      if (response.success) {
        setExtension(response.extension);
      } else {
        toast.error('Failed to fetch extension details');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching extension details:', error);
      toast.error('Error loading extension details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const response = await extensionsAPI.getDownload(id);
      
      if (response.success && response.downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = response.downloadUrl;
        link.download = `${extension.name}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('Download started');
      } else {
        toast.error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleEdit = () => {
    navigate(`/update-extension/${id}`);
  };

  const handleCopyLink = () => {
    if (extension.chromeWebStoreLink) {
      navigator.clipboard.writeText(extension.chromeWebStoreLink);
      toast.success('Chrome Web Store link copied to clipboard');
    } else {
      toast.error('No Chrome Web Store link available');
    }
  };

  const handleOpenChromeWebStore = () => {
    if (extension.chromeWebStoreLink) {
      window.open(extension.chromeWebStoreLink, '_blank');
    } else {
      toast.error('No Chrome Web Store link available');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10B981';
      case 'inactive': return '#EF4444';
      case 'draft': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  if (loading) {
    return (
      <div className="extension-details-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading extension details...</p>
        </div>
      </div>
    );
  }

  if (!extension) {
    return (
      <div className="extension-details-container">
        <div className="error-message">
          <h2>Extension Not Found</h2>
          <p>The extension you're looking for doesn't exist or you don't have access to it.</p>
          <button onClick={handleBack} className="btn btn-primary">
            <FiArrowLeft /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="extension-details-container">
      {/* Header */}
      <div className="extension-header">
        <button onClick={handleBack} className="back-button">
          <FiArrowLeft /> Back to Dashboard
        </button>
        <div className="extension-title">
          <div className="extension-icon">
            <img 
              src={extension.icon || 'https://via.placeholder.com/80x80/039BE5/FFFFFF?text=FC'} 
              alt={extension.name}
            />
          </div>
          <div className="extension-info">
            <h1>{extension.name}</h1>
            <p className="extension-description">{extension.description}</p>
            <div className="extension-tags">
              {extension.isWhitelabeled && (
                <span className="tag whitelabel">
                  <FiStar /> Whitelabeled
                </span>
              )}
              {extension.isUpgradeable && (
                <span className="tag upgrade">
                  <FiActivity /> Upgradeable
                </span>
              )}
              <span className="tag status" style={{ backgroundColor: getStatusColor(extension.status) }}>
                {extension.status}
              </span>
            </div>
          </div>
        </div>
        <div className="extension-actions">
          {/* Edit button for Extension Creators and superadmin/admin */}
          {(partner?.role === 'superadmin' || partner?.role === 'admin' || 
            (partner?.role === 'partner' && ['ceo', 'whitelabeler'].includes(partner?.partnerType))) && (
            <button 
              onClick={handleEdit}
              className="btn btn-secondary edit-btn"
            >
              <FiEdit />
              Edit Extension
            </button>
          )}
          {partner && (partner.role === 'partner' && ['ceo', 'whitelabeler'].includes(partner.partnerType)) && (
            <button 
              onClick={handleDownload} 
              disabled={downloading}
              className="btn btn-primary download-btn"
            >
              <FiDownload />
              {downloading ? 'Downloading...' : 'Download Extension'}
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="extension-content">
        <div className="content-grid">
          {/* Basic Information */}
          <div className="info-card">
            <div className="card-header">
              <FiPackage />
              <h3>Basic Information</h3>
            </div>
            <div className="card-content">
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{extension.name}</span>
              </div>
              <div className="info-row">
                <span className="label">Description:</span>
                <span className="value">{extension.description || 'No description available'}</span>
              </div>
              <div className="info-row">
                <span className="label">Category:</span>
                <span className="value">{extension.category || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="label">Version:</span>
                <span className="value">{extension.version || '1.0.0'}</span>
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span className="value">
                  <span className="status-badge" style={{ backgroundColor: getStatusColor(extension.status) }}>
                    {extension.status}
                  </span>
                </span>
              </div>
              {extension.chromeWebStoreLink && (
                <div className="info-row">
                  <span className="label">Chrome Web Store:</span>
                  <div className="value">
                    <div className="chrome-store-buttons">
                      <button 
                        onClick={handleCopyLink}
                        className="btn btn-small btn-outline"
                        title="Copy Chrome Web Store link"
                      >
                        <FiCopy />
                        Copy Link
                      </button>
                      <button 
                        onClick={handleOpenChromeWebStore}
                        className="btn btn-small btn-outline"
                        title="Open Chrome Web Store"
                      >
                        <FiExternalLink />
                        Open Chrome Web Store
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Statistics */}
          <div className="info-card">
            <div className="card-header">
              <FiActivity />
              <h3>Statistics</h3>
            </div>
            <div className="card-content">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">{extension.totalUsers || 0}</div>
                  <div className="stat-label">Total Users</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{extension.assignedPartners?.length || 0}</div>
                  <div className="stat-label">Assigned Partners</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{extension.plans?.length || 0}</div>
                  <div className="stat-label">Plans Available</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">{extension.features?.length || 0}</div>
                  <div className="stat-label">Features</div>
                </div>
              </div>
            </div>
          </div>



          {/* Features */}
          {extension.features && extension.features.length > 0 && (
            <div className="info-card">
              <div className="card-header">
                <FiSettings />
                <h3>Features</h3>
              </div>
              <div className="card-content">
                <div className="features-list">
                  {extension.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <span className="feature-dot"></span>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Assigned Partners */}
          {extension.assignedPartners && extension.assignedPartners.length > 0 && (
            <div className="info-card">
              <div className="card-header">
                <FiUsers />
                <h3>Assigned Partners</h3>
              </div>
              <div className="card-content">
                <div className="partners-list">
                  {extension.assignedPartners.map((partner, index) => (
                    <div key={index} className="partner-item">
                      <div className="partner-info">
                        <div className="partner-name">
                          {partner.partnerId?.firstName} {partner.partnerId?.lastName}
                        </div>
                        <div className="partner-email">{partner.partnerId?.email}</div>
                      </div>
                      <div className="partner-company">{partner.partnerId?.company || 'N/A'}</div>
                      <div className="assignment-date">
                        Assigned: {formatDate(partner.assignedAt)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="info-card">
            <div className="card-header">
              <FiCalendar />
              <h3>Timeline</h3>
            </div>
            <div className="card-content">
              <div className="info-row">
                <span className="label">Created:</span>
                <span className="value">{formatDate(extension.createdAt)}</span>
              </div>
              <div className="info-row">
                <span className="label">Last Updated:</span>
                <span className="value">{formatDate(extension.updatedAt)}</span>
              </div>
              {extension.createdBy && (
                <div className="info-row">
                  <span className="label">Created By:</span>
                  <span className="value">
                    {extension.createdBy.firstName} {extension.createdBy.lastName}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExtensionDetails; 